/**
 * School permissions — source of truth for flight-school organizations.
 *
 * Schools have four roles: `admin`, `cfi`, `student`, and `dispatch`. Schools
 * are the superset of capabilities — they add training, student/instructor
 * assignments, availability scheduling, and the dispatch center on top of the
 * shared booking/aircraft/maintenance base. The cross-vertical resolver
 * (features/shared/permissions) dispatches to this table for school
 * organizations.
 */
import { Permission } from '../../../core/permissions';

export const SCHOOL_ROLES = ['admin', 'cfi', 'student', 'dispatch', 'maintenance'] as const;
export type SchoolRole = typeof SCHOOL_ROLES[number];

export const SCHOOL_ROLE_PERMISSIONS: Record<SchoolRole, Permission[]> = {
  admin: [
    // Full access to everything
    'BOOKINGS:CREATE', 'BOOKINGS:READ', 'BOOKINGS:UPDATE', 'BOOKINGS:DELETE', 'BOOKINGS:CANCEL_OTHERS', 'BOOKINGS:DISPATCH_OVERRIDE',
    'AIRCRAFT:CREATE', 'AIRCRAFT:READ', 'AIRCRAFT:UPDATE', 'AIRCRAFT:DELETE',
    'MEMBERS:CREATE', 'MEMBERS:READ', 'MEMBERS:UPDATE', 'MEMBERS:DELETE', 'MEMBERS:ASSIGN_ROLES',
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK', 'MAINTENANCE:UPDATE_SQUAWK', 'MAINTENANCE:CLOSE_SQUAWK', 'MAINTENANCE:APPROVE_WORK',
    'FLIGHT_PLANS:CREATE', 'FLIGHT_PLANS:READ', 'FLIGHT_PLANS:UPDATE', 'FLIGHT_PLANS:DELETE', 'FLIGHT_PLANS:APPROVE', 'FLIGHT_PLANS:DISPATCH_OVERRIDE',
    'ASSIGNMENTS:CREATE', 'ASSIGNMENTS:READ', 'ASSIGNMENTS:UPDATE', 'ASSIGNMENTS:DELETE',
    'TRAINING:READ', 'TRAINING:UPDATE_PROGRESS', 'TRAINING:ENDORSE', 'TRAINING:APPROVE_SOLO',
    // Admin: Can see CFI and Student availability, can update and delete
    'AVAILABILITY:READ_ALL', 'AVAILABILITY:UPDATE_OTHERS', 'AVAILABILITY:DELETE_OTHERS', 'AVAILABILITY:APPROVE_TIMEOFF', 'AVAILABILITY:MANAGE_SYSTEM',
    'ORG:READ', 'ORG:UPDATE', 'ORG:MANAGE_SETTINGS', 'ORG:VIEW_ANALYTICS',
    'DOCUMENTS:READ', 'DOCUMENTS:UPLOAD', 'DOCUMENTS:VERIFY', 'DOCUMENTS:DELETE'
  ],

  cfi: [
    // CFI permissions - can instruct students and manage training
    'BOOKINGS:CREATE', 'BOOKINGS:READ', 'BOOKINGS:UPDATE', 'BOOKINGS:DELETE', 'BOOKINGS:CANCEL_OTHERS',
    'AIRCRAFT:READ', 'AIRCRAFT:UPDATE_HOURS',
    'MEMBERS:READ', // Can view student profiles
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK',
    'FLIGHT_PLANS:CREATE', 'FLIGHT_PLANS:READ', 'FLIGHT_PLANS:UPDATE', 'FLIGHT_PLANS:DELETE', 'FLIGHT_PLANS:APPROVE',
    'ASSIGNMENTS:READ', // Can view their assignments
    'TRAINING:READ', 'TRAINING:UPDATE_PROGRESS', 'TRAINING:ENDORSE', 'TRAINING:APPROVE_SOLO',
    // CFI: Can see assigned students' availability, can see and update own availability
    'AVAILABILITY:READ_OWN', 'AVAILABILITY:UPDATE_OWN', 'AVAILABILITY:DELETE_OWN', 'AVAILABILITY:READ_ALL', 'AVAILABILITY:APPROVE_TIMEOFF',
    'ORG:READ',
    'DOCUMENTS:READ', 'DOCUMENTS:UPLOAD', 'DOCUMENTS:VERIFY'
  ],

  student: [
    // Student permissions - view only on bookings; dispatch/admin creates all bookings for students
    'BOOKINGS:READ',
    'AIRCRAFT:READ',
    // Removed MAINTENANCE permissions - students should not access maintenance features
    'FLIGHT_PLANS:CREATE', 'FLIGHT_PLANS:READ', 'FLIGHT_PLANS:UPDATE', 'FLIGHT_PLANS:DELETE',
    'ASSIGNMENTS:READ', // Can view their own assignments
    'TRAINING:READ', // Can view their own progress
    // Student: Can see and update own availability
    'AVAILABILITY:READ_OWN', 'AVAILABILITY:UPDATE_OWN', 'AVAILABILITY:DELETE_OWN', 'AVAILABILITY:REQUEST_TIMEOFF',
    'ORG:READ',
    'DOCUMENTS:READ', 'DOCUMENTS:UPLOAD'
  ],

  dispatch: [
    // Dispatcher permissions - can coordinate and override when needed
    'BOOKINGS:CREATE', 'BOOKINGS:READ', 'BOOKINGS:UPDATE', 'BOOKINGS:DELETE', 'BOOKINGS:CANCEL_OTHERS', 'BOOKINGS:DISPATCH_OVERRIDE',
    'AIRCRAFT:CREATE', 'AIRCRAFT:READ', 'AIRCRAFT:UPDATE',
    'MEMBERS:CREATE', 'MEMBERS:READ',
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK', 'MAINTENANCE:UPDATE_SQUAWK', 'MAINTENANCE:CLOSE_SQUAWK',
    'FLIGHT_PLANS:CREATE', 'FLIGHT_PLANS:READ', 'FLIGHT_PLANS:UPDATE', 'FLIGHT_PLANS:DELETE', 'FLIGHT_PLANS:DISPATCH_OVERRIDE',
    'ASSIGNMENTS:CREATE', 'ASSIGNMENTS:READ', 'ASSIGNMENTS:UPDATE', 'ASSIGNMENTS:DELETE',
    'TRAINING:READ', 'TRAINING:UPDATE_PROGRESS',
    // Dispatcher: Can see CFI and Student availability, can update and delete
    'AVAILABILITY:READ_ALL', 'AVAILABILITY:UPDATE_OTHERS', 'AVAILABILITY:DELETE_OTHERS', 'AVAILABILITY:APPROVE_TIMEOFF', 'AVAILABILITY:MANAGE_SYSTEM',
    'ORG:READ', 'ORG:VIEW_ANALYTICS',
    'DOCUMENTS:READ', 'DOCUMENTS:VERIFY'
  ],

  maintenance: [
    // School maintenance: aircraft squawks and work orders; can view bookings for coordination.
    // Adjust this list independently of club/maintenance if school rules diverge.
    'BOOKINGS:READ',
    'AIRCRAFT:READ', 'AIRCRAFT:UPDATE',
    'MEMBERS:READ',
    'MAINTENANCE:READ', 'MAINTENANCE:CREATE_SQUAWK', 'MAINTENANCE:UPDATE_SQUAWK', 'MAINTENANCE:CLOSE_SQUAWK', 'MAINTENANCE:APPROVE_WORK',
    'ORG:READ',
    'DOCUMENTS:READ'
  ]
};

/**
 * Check if a user can manage students within a school organization.
 * Admins, dispatch, and CFIs may manage students; returns false for non-school organizations.
 */
export function canManageSchoolStudents(
  user: { role: string } | null,
  org: { type: string } | null
): boolean {
  if (!user || !org) return false;
  if (org.type !== 'school') return false;
  switch (user.role) {
    case 'admin':
    case 'dispatch':
    case 'cfi':
      return true;
    default:
      return false;
  }
}
